/*
 * Planigo
 * Auteur  : Stéphane Raverdy
 * Société : SCIAD 2026
 */
import { useState, useEffect } from 'react';
import { adminApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Buildings, Key, ShieldCheck, Users } from '@phosphor-icons/react';
import { APP_VERSION } from '../lib/version';

export default function AboutPage() {
  const [license, setLicense] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [licRes] = await Promise.all([
          adminApi.getLicense(),
        ]);
        setLicense(licRes.data);
      } catch { }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;

  const appName = 'Planigo';
  const licenseTypeLabel = { standard: 'Standard', premium: 'Premium', enterprise: 'Entreprise' };
  const isExpired = license?.license_expiry ? new Date(license.license_expiry) < new Date() : false;
  const daysRemaining = license?.license_expiry ? Math.ceil((new Date(license.license_expiry) - new Date()) / (1000 * 60 * 60 * 24)) : null;

  return (
    <div className="space-y-6" data-testid="about-page">
      <div className="text-center py-8">
        <img src="/planigo-logo.png" alt="Planigo" className="h-32 max-w-[420px] object-contain mx-auto mb-4" />
        <p className="text-slate-600 font-medium mt-1">Planification des équipes, projets & interventions</p>
        <Badge className="mt-3 bg-blue-100 text-blue-700 text-sm px-4 py-1">Version {APP_VERSION}</Badge>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
        {/* License Card */}
        <Card className="card-surface">
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm flex items-center gap-2"><Key size={16} className="text-amber-600" />Licence</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-slate-500">Code client</span>
              <span className="text-xs font-mono font-bold text-slate-800">{license?.client_code || '-'}</span>
            </div>
            {license?.license_key && (
              <div className="flex justify-between items-center">
                <span className="text-xs text-slate-500">Cle de licence</span>
                <span className="text-xs font-mono font-bold text-blue-700">{license.license_key}</span>
              </div>
            )}
            <div className="flex justify-between items-center">
              <span className="text-xs text-slate-500">Type</span>
              <Badge className={`text-xs ${license?.license_type === 'enterprise' ? 'bg-purple-100 text-purple-700' : license?.license_type === 'premium' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700'}`}>
                {licenseTypeLabel[license?.license_type] || 'Standard'}
              </Badge>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-slate-500">Expiration</span>
              <span className={`text-xs font-medium ${isExpired ? 'text-red-600' : 'text-green-600'}`}>
                {license?.license_expiry ? new Date(license.license_expiry).toLocaleDateString('fr-FR') : '-'}
              </span>
            </div>
            {daysRemaining !== null && (
              <div className={`text-center py-2 rounded text-xs font-medium ${isExpired ? 'bg-red-50 text-red-700' : daysRemaining < 30 ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700'}`}>
                {isExpired ? `Expiree depuis ${Math.abs(daysRemaining)} jour(s)` : `${daysRemaining} jour(s) restant(s)`}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Company Card */}
        <Card className="card-surface">
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm flex items-center gap-2"><Buildings size={16} className="text-blue-600" />Societe</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-slate-500">Nom</span>
              <span className="text-xs font-medium">{license?.company_name || 'Non configure'}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-slate-500">Adresse</span>
              <span className="text-xs text-right max-w-[60%]">{license?.company_address || '-'}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-xs text-slate-500">SIRET</span>
              <span className="text-xs font-mono">{license?.company_siret || '-'}</span>
            </div>
          </CardContent>
        </Card>

        {/* Usage Card */}
        <Card className="card-surface">
          <CardHeader className="py-3 px-4">
            <CardTitle className="text-sm flex items-center gap-2"><Users size={16} className="text-green-600" />Utilisation</CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-slate-500">Utilisateurs</span>
              <span className="text-xs font-medium">{license?.current_users || 0} / {license?.max_users || 25}</span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-2 mt-1">
              <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${Math.min(100, ((license?.current_users || 0) / (license?.max_users || 25)) * 100)}%` }}></div>
            </div>
            <div className="flex justify-between items-center pt-2">
              <span className="text-xs text-slate-500">Statut</span>
              <Badge className={`text-xs ${isExpired ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                <ShieldCheck size={12} className="mr-1" />{isExpired ? 'Expiree' : 'Active'}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="text-center text-xs text-slate-400 pt-6">
        <p>{appName} v{APP_VERSION} - Planification des équipes, projets & interventions</p>
        <p className="mt-1">Tous droits réservés {new Date().getFullYear()}</p>
        <p className="mt-1 font-medium text-slate-500">Développé par la SCIAD</p>
      </div>
    </div>
  );
}
